import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Mail, Lock, User, Eye, EyeOff, KeyRound, Globe, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { setToken } from '@/utils/tokenManager';
import FieldError from '../ui/FieldError';
import SearchableSelect from '../SearchableSelect';
import COUNTRIES from '../../data/countries';
import { validateRequired, validateMinLength, validateEmail, validatePassword, validatePasswordMatch } from '../../utils/validation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COUNTRY_OPTIONS = COUNTRIES.map(c => ({ value: c, label: c, searchKey: c }));

const RegisterForm = ({ isLoading, setIsLoading, navigate }) => {
  // Read an optional admin-issued registration token from the URL.
  // When present, the user is registering via an approved invitation link
  // (e.g., from an advisor introduction) — beta code is not required.
  const invitationToken = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get('token') || '').trim();
    } catch {
      return '';
    }
  }, []);
  const hasInvitation = invitationToken.length > 0;

  const [registerData, setRegisterData] = useState({
    name: '', email: '', country: '', password: '', confirmPassword: '', betaCode: '', agreeToTerms: false
  });
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({});

  const errors = {
    name: touched.name ? (validateRequired(registerData.name, 'Full name') || validateMinLength(registerData.name, 2, 'Full name')) : '',
    email: touched.email ? validateEmail(registerData.email) : '',
    country: touched.country ? validateRequired(registerData.country, 'Country') : '',
    password: touched.password ? validatePassword(registerData.password) : '',
    confirmPassword: touched.confirmPassword ? validatePasswordMatch(registerData.password, registerData.confirmPassword) : '',
    betaCode: (!hasInvitation && touched.betaCode) ? validateRequired(registerData.betaCode, 'Beta access code') : '',
  };

  const hasErrors = Object.values(errors).some(e => e);

  const handleRegister = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, country: true, password: true, confirmPassword: true, betaCode: !hasInvitation });

    const allErrors = {
      name: validateRequired(registerData.name, 'Full name') || validateMinLength(registerData.name, 2, 'Full name'),
      email: validateEmail(registerData.email),
      country: validateRequired(registerData.country, 'Country'),
      password: validatePassword(registerData.password),
      confirmPassword: validatePasswordMatch(registerData.password, registerData.confirmPassword),
      betaCode: hasInvitation ? '' : validateRequired(registerData.betaCode, 'Beta access code'),
    };
    const firstError = Object.values(allErrors).find(e => e);
    if (firstError) { toast.error(firstError); return; }
    if (!registerData.agreeToTerms) { toast.error('You must agree to the Privacy Policy and Terms of Service'); return; }

    setIsLoading(true);
    try {
      const body = {
        name: registerData.name,
        email: registerData.email,
        country: registerData.country,
        password: registerData.password,
      };
      if (hasInvitation) {
        body.registration_token = invitationToken;
      } else {
        body.beta_code = registerData.betaCode;
      }
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || 'Registration failed'); }
      const userData = await response.json();
      if (userData.token) { setToken(userData.token); }
      toast.success('Registration successful! Redirecting...', { duration: 2500 });
      setTimeout(() => { navigate('/', { replace: true }); }, 1200);
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const onBlur = (field) => setTouched(t => ({ ...t, [field]: true }));
  const onChange = (field, value) => setRegisterData(prev => ({ ...prev, [field]: value }));

  // No automatic side-effect; the invitation indicator is rendered inline.
  // (useEffect kept for parity if future analytics is added.)
  useEffect(() => { /* placeholder */ }, [hasInvitation]);

  return (
    <form onSubmit={handleRegister} className="space-y-4">
      {hasInvitation && (
        <p
          className="text-xs text-emerald-700 flex items-center gap-1.5"
          data-testid="register-invitation-recognised"
        >
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          Your invitation has been recognised — no beta code required.
        </p>
      )}
      <div className="space-y-2">
        <Label htmlFor="register-name" className="portfolio-input-label">Full Name <span className="text-red-500">*</span></Label>
        <div className="relative">
          <User className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input id="register-name" data-testid="register-name-input" type="text" placeholder="Enter your full name"
            value={registerData.name} onChange={(e) => onChange('name', e.target.value)} onBlur={() => onBlur('name')}
            className={`pl-10 portfolio-input ${errors.name ? 'border-red-400' : ''}`} />
        </div>
        <FieldError error={errors.name} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-email" className="portfolio-input-label">Email Address <span className="text-red-500">*</span></Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input id="register-email" data-testid="register-email-input" type="text" placeholder="Enter your email address"
            value={registerData.email} onChange={(e) => onChange('email', e.target.value)} onBlur={() => onBlur('email')}
            className={`pl-10 portfolio-input ${errors.email ? 'border-red-400' : ''}`} />
        </div>
        <FieldError error={errors.email} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-country" className="portfolio-input-label">Country <span className="text-red-500">*</span></Label>
        <div className="relative">
          <Globe className="absolute left-3 top-3 w-5 h-5 text-slate-400 pointer-events-none z-10" />
          <div className={`[&_button]:pl-10 [&_button]:portfolio-input ${errors.country ? '[&_button]:border-red-400' : ''}`}>
            <SearchableSelect
              value={registerData.country}
              onValueChange={(v) => { onChange('country', v); onBlur('country'); }}
              options={COUNTRY_OPTIONS}
              placeholder="Select your country"
              searchPlaceholder="Search country..."
              emptyMessage="No country found."
              dataTestId="register-country-select"
            />
          </div>
        </div>
        <FieldError error={errors.country} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-password" className="portfolio-input-label">Password <span className="text-red-500">*</span></Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input id="register-password" data-testid="register-password-input" type={showRegisterPassword ? 'text' : 'password'} placeholder="Create a password"
            value={registerData.password} onChange={(e) => onChange('password', e.target.value)} onBlur={() => onBlur('password')}
            className={`pl-10 pr-10 portfolio-input ${errors.password ? 'border-red-400' : ''}`} />
          <button type="button" tabIndex={-1} onClick={() => setShowRegisterPassword(!showRegisterPassword)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-300">
            {showRegisterPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <FieldError error={errors.password} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-confirm-password" className="portfolio-input-label">Confirm Password <span className="text-red-500">*</span></Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input id="register-confirm-password" data-testid="register-confirm-password-input" type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirm your password"
            value={registerData.confirmPassword} onChange={(e) => onChange('confirmPassword', e.target.value)} onBlur={() => onBlur('confirmPassword')}
            className={`pl-10 pr-10 portfolio-input ${errors.confirmPassword ? 'border-red-400' : ''}`} />
          <button type="button" tabIndex={-1} onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-300">
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <FieldError error={errors.confirmPassword} />
      </div>
      {!hasInvitation && (
        <div className="space-y-2">
          <Label htmlFor="register-beta-code" className="portfolio-input-label">Beta Access Code <span className="text-red-500">*</span></Label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
            <Input id="register-beta-code" data-testid="register-beta-code-input" type="text" placeholder="Enter your beta access code"
              value={registerData.betaCode} onChange={(e) => onChange('betaCode', e.target.value)} onBlur={() => onBlur('betaCode')}
              className={`pl-10 portfolio-input ${errors.betaCode ? 'border-red-400' : ''}`} />
          </div>
          <FieldError error={errors.betaCode} />
          <p className="text-xs text-slate-500">Required during the beta period</p>
        </div>
      )}
      <div className="flex items-start space-x-2 mt-4">
        <input type="checkbox" id="agree-terms" data-testid="register-agree-terms" checked={registerData.agreeToTerms} onChange={(e) => onChange('agreeToTerms', e.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
        <label htmlFor="agree-terms" className="text-xs text-slate-600 leading-relaxed">
          I agree to the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">Privacy Policy</a> and <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">Terms of Service</a> <span className="text-red-500">*</span>
        </label>
      </div>
      <Button type="submit" data-testid="register-submit-btn" disabled={isLoading || hasErrors} className="w-full portfolio-button-primary mt-6">
        {isLoading ? 'Creating Account...' : 'Create Account'}
      </Button>
    </form>
  );
};

export default RegisterForm;
